import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, photoSrc } from "../lib/api";
import { useSocket } from "../context/SocketContext";
import { TopBar, BottomNav } from "../components/Shell";
import { Heart, Loader2, MessageCircle } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "now";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

export default function Matches() {
  const navigate = useNavigate();
  const { isOnline } = useSocket();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/matches");
      setMatches(data.matches);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const fresh = matches.filter((m) => !m.last_message);
  const convos = matches.filter((m) => m.last_message);

  const avatar = (u) => u.photos?.find((p) => p.is_primary)?.url || u.photos?.[0]?.url;

  return (
    <>
      <TopBar title="Matches" />
      <div className="flex-1 overflow-y-auto no-scrollbar">
        {loading ? (
          <div className="h-full grid place-items-center"><Loader2 className="w-7 h-7 text-rose-500 animate-spin" /></div>
        ) : matches.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-10 gap-3">
            <div className="w-18 h-18 p-5 rounded-full bg-rose-500/10"><Heart className="w-8 h-8 text-rose-400" fill="currentColor" /></div>
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>No matches yet</h3>
            <p className="text-slate-400 text-sm">Start swiping to find your people in Kalaburagi.</p>
            <button onClick={() => navigate("/")} className="mt-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-rose-600 text-white text-sm font-semibold">Discover people</button>
          </div>
        ) : (
          <div className="px-4 py-4">
            {fresh.length > 0 && (
              <div className="mb-6">
                <h4 className="text-xs font-semibold tracking-wider uppercase text-rose-400 mb-3 px-1">New Matches</h4>
                <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
                  {fresh.map((m) => (
                    <button key={m.match_id} data-testid="match-item-card" onClick={() => navigate(`/chat/${m.match_id}`)}
                      className="flex flex-col items-center gap-1.5 shrink-0 w-[4.5rem]">
                      <div className="relative w-16 h-16 rounded-full p-0.5 bg-gradient-to-tr from-rose-500 to-amber-400">
                        <img src={photoSrc(avatar(m.user))} alt="" className="w-full h-full rounded-full object-cover border-2 border-[#0D0C12]" />
                        {(isOnline(m.user.user_id) || m.online) && <span data-testid="match-online-dot" className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-[#0D0C12]" />}
                      </div>
                      <span className="text-xs text-slate-300 truncate w-full text-center">{m.user.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <h4 className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2 px-1">Messages</h4>
            {convos.length === 0 ? (
              <p className="text-slate-500 text-sm px-1 py-4">No conversations yet — say hi to a new match!</p>
            ) : (
              <div className="flex flex-col">
                {convos.map((m) => (
                  <button key={m.match_id} data-testid="chat-item-card" onClick={() => navigate(`/chat/${m.match_id}`)}
                    className="flex items-center gap-3 py-3 px-1 border-b border-white/5 text-left active:bg-white/5 rounded-xl transition-colors">
                    <div className="relative shrink-0">
                      <img src={photoSrc(avatar(m.user))} alt="" className="w-14 h-14 rounded-full object-cover" />
                      {(isOnline(m.user.user_id) || m.online) && <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-[#0D0C12]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-white truncate">{m.user.name}</span>
                        <span className="text-xs text-slate-500 shrink-0">{timeAgo(m.last_message?.created_at)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <p className={`text-sm truncate ${m.unread_count > 0 ? "text-white font-medium" : "text-slate-400"}`}>{m.last_message?.text}</p>
                        {m.unread_count > 0 && (
                          <span data-testid="unread-badge" className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold grid place-items-center">{m.unread_count}</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <BottomNav />
    </>
  );
}
